"use client";

import {
  Box,
  Grid,
  Typography,
  Container,
  Button,
  Card,
  CardContent,
  useTheme,
  alpha,
} from "@mui/material";
import React from "react";
import { shades } from "@/styles/theme";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CodeIcon from "@mui/icons-material/Code";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import SupportIcon from "@mui/icons-material/Support";
import PopoverForm from "./PopoverForm";

const newServices = [
  {
    title: "Business Process Automation",
    description:
      "We help startups and SMEs eliminate repetitive tasks and streamline operations through powerful no-code and custom automation systems. From CRM to invoicing, we make your workflows faster and smarter.",
    icon: <SmartToyIcon />,
    features: [
      "Workflow & task automation",
      "CRM + Email integration",
      "Lead management & onboarding systems",
      "AI assistants & chatbots",
    ],
    cta: "Discover Automation",
    color: "primary",
    ctaLink: "/services/automation",
  },
  {
    title: "Web Systems & Applications",
    description:
      "Custom-built web platforms that drive business growth — from conversion-focused websites to scalable SaaS dashboards. We use cutting-edge technologies like Next.js, React, and TypeScript to deliver speed, performance, and scalability.",
    icon: <CodeIcon />,
    features: [
      "Custom Web Applications",
      "E-commerce Platforms (Shopify, WooCommerce)",
      "Progressive Web Apps (PWAs)",
      "SaaS & Client Portals",
    ],
    cta: "Explore Web Solutions",
    color: "secondary",
    ctaLink: "/services/web-development",
  },
  {
    title: "Automation Audit & Strategy",
    description:
      "A 360° review of your business workflows to uncover automation opportunities, cut inefficiencies, and design a roadmap for measurable ROI. Perfect for teams ready to scale through intelligent automation.",
    icon: <SupportIcon />,
    features: [
      "Automation opportunity mapping",
      "Workflow design roadmap",
      "KPI-based performance tracking",
      "ROI-focused strategy delivery",
    ],
    cta: "Get Your Audit",
    color: "info",
    ctaLink: "/services/it-support",
  },
];

const Services = () => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        py: { xs: 6, md: 10 },
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.background.default,
          1
        )} 0%, ${alpha(theme.palette.primary.main, 0.03)} 100%)`,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background Elements */}
      <Box
        sx={{
          position: "absolute",
          top: -100,
          right: -100,
          width: 300,
          height: 300,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${alpha(
            theme.palette.primary.main,
            0.05
          )} 0%, transparent 70%)`,
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: -50,
          left: -50,
          width: 200,
          height: 200,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${alpha(
            theme.palette.secondary.main,
            0.05
          )} 0%, transparent 70%)`,
          zIndex: 0,
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        {/* Header Section */}
        <Box sx={{ textAlign: "center", mb: { xs: 4, md: 8 } }}>
          <Typography
            variant="h2"
            component="h3"
            gutterBottom
            sx={{
              fontWeight: 700,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
              fontSize: { xs: "2.5rem", md: "3rem" },
            }}
          >
            Our Core Services
          </Typography>
          <Typography
            variant="h6"
            component={"h4"}
            color={shades.primary[400]}
            sx={{
              maxWidth: 600,
              mx: "auto",
              fontSize: { xs: "1.1rem", md: "1.25rem" },
              lineHeight: 1.6,
            }}
          >
            End-to-end digital solutions tailored to drive your business growth
            and efficiency
          </Typography>
        </Box>

        {/* Services Grid */}
        <Grid container spacing={4} justifyContent="center">
          {newServices.map((service, index) => (
            <Grid size={{ xs: 12, md: 4 }} key={index}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  background: `linear-gradient(135deg, ${alpha(
                    shades.primary[500],
                    0.04
                  )} 0%, ${alpha(shades.secondary[500], 0.05)} 100%)`,
                  transition: "all 0.3s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: `0 20px 40px ${alpha(
                      theme.palette.primary.main,
                      0.15
                    )}`,
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                  },
                }}
              >
                <CardContent
                  sx={{
                    p: 4,
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/* Service Icon */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      backgroundColor: alpha(
                        service.color === "primary"
                          ? theme.palette.primary.main
                          : service.color === "secondary"
                          ? theme.palette.secondary.main
                          : theme.palette.info.main,
                        0.1
                      ),
                      color:
                        service.color === "primary"
                          ? theme.palette.primary.main
                          : service.color === "secondary"
                          ? theme.palette.secondary.main
                          : theme.palette.info.main,
                      mb: 3,
                      "& .MuiSvgIcon-root": {
                        fontSize: 40,
                      },
                    }}
                  >
                    {service.icon}
                  </Box>

                  {/* Service Title */}
                  <Typography
                    variant="h5"
                    component="h3"
                    gutterBottom
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: "1.5rem", md: "1.75rem" },
                    }}
                  >
                    {service.title}
                  </Typography>

                  {/* Service Description */}
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    paragraph
                    sx={{
                      lineHeight: 1.4,
                      flexGrow: 1,
                    }}
                  >
                    {service.description}
                  </Typography>

                  {/* Features List */}
                  <Box sx={{ mb: 3 }}>
                    {service.features.map((feature, featureIndex) => (
                      <Typography
                        key={featureIndex}
                        variant="body1"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: 1,
                          color: "text.primary",
                          fontWeight: 500,
                          "&:before": {
                            content: '"▸"',
                            color:
                              service.color === "primary"
                                ? theme.palette.primary.main
                                : service.color === "secondary"
                                ? theme.palette.secondary.main
                                : theme.palette.info.main,
                            fontWeight: "bold",
                            mr: 1,
                          },
                        }}
                      >
                        {feature}
                      </Typography>
                    ))}
                  </Box>

                  {/* CTA Button */}
                  <Button
                    variant="outlined"
                    endIcon={<ArrowForwardIcon />}
                    fullWidth
                    sx={{
                      mt: "auto",
                      py: 1.5,
                      borderColor:
                        service.color === "primary"
                          ? theme.palette.primary.main
                          : service.color === "secondary"
                          ? theme.palette.secondary.main
                          : theme.palette.info.main,
                      color:
                        service.color === "primary"
                          ? theme.palette.primary.main
                          : service.color === "secondary"
                          ? theme.palette.secondary.main
                          : theme.palette.info.main,
                      "&:hover": {
                        backgroundColor:
                          service.color === "primary"
                            ? alpha(theme.palette.primary.main, 0.1)
                            : service.color === "secondary"
                            ? alpha(theme.palette.secondary.main, 0.1)
                            : alpha(theme.palette.info.main, 0.1),
                        transform: "translateX(4px)",
                      },
                      transition: "all 0.3s ease",
                    }}
                    href={service.ctaLink}
                  >
                    {service.cta}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Bottom CTA Section */}
        <Box
          sx={{
            textAlign: "center",
            mt: 8,
            p: 4,
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              0.05
            )} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Typography
            variant="h5"
            component="h3"
            gutterBottom
            sx={{ fontWeight: 600 }}
          >
            Need a Custom Solution?
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mb: 3, maxWidth: 500, mx: "auto" }}
          >
            We specialize in creating tailored solutions that perfectly fit your
            unique business requirements.
          </Typography>
          <PopoverForm
            triggerElement={
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  px: 4,
                  py: 1.5,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: `0 8px 25px ${alpha(
                      theme.palette.primary.main,
                      0.3
                    )}`,
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Get Free Consultation
              </Button>
            }
          />
        </Box>
      </Container>
    </Box>
  );
};

export default Services;
